import { createI18n } from 'vue-i18n'

export type SupportedLocale = 'en' | 'pt' | 'es' | 'fr'

export const SUPPORTED_LOCALES: SupportedLocale[] = ['en', 'pt', 'es', 'fr']

export const DEFAULT_LOCALE: SupportedLocale = 'en'

export function getLocaleFromUrl(): SupportedLocale {
  const urlParams = new URLSearchParams(window.location.search)
  const locale = urlParams.get('locale') || urlParams.get('lang')
  if (locale && SUPPORTED_LOCALES.includes(locale as SupportedLocale)) {
    return locale as SupportedLocale
  }
  return DEFAULT_LOCALE
}

export async function loadLocaleMessages(locale: SupportedLocale): Promise<Record<string, any>> {
  try {
    const response = await fetch(`/locales/${locale}.json`)
    if (!response.ok) {
      console.warn(`Failed to load locale ${locale}, falling back to ${DEFAULT_LOCALE}`)
      if (locale !== DEFAULT_LOCALE) {
        return loadLocaleMessages(DEFAULT_LOCALE)
      }
      return {}
    }
    return await response.json()
  } catch (error) {
    console.error(`Error loading locale ${locale}:`, error)
    if (locale !== DEFAULT_LOCALE) {
      return loadLocaleMessages(DEFAULT_LOCALE)
    }
    return {}
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let i18nInstance: any = null

export async function setupI18n() {
  const locale = getLocaleFromUrl()
  const messages = await loadLocaleMessages(locale)

  i18nInstance = createI18n({
    legacy: false,
    locale,
    fallbackLocale: DEFAULT_LOCALE,
    messages: {
      [locale]: messages
    },
    silentTranslationWarn: true,
    silentFallbackWarn: true
  })

  return i18nInstance
}

export async function setLocale(locale: SupportedLocale): Promise<void> {
  if (!i18nInstance) {
    console.warn('i18n not initialized')
    return
  }

  if (!SUPPORTED_LOCALES.includes(locale)) {
    console.warn(`Locale ${locale} not supported, using ${DEFAULT_LOCALE}`)
    locale = DEFAULT_LOCALE
  }

  const i18n = i18nInstance.global
  
  if (!i18n.availableLocales.includes(locale)) {
    const messages = await loadLocaleMessages(locale)
    i18n.setLocaleMessage(locale, messages)
  }

  i18n.locale.value = locale
}

export function getI18n() {
  return i18nInstance
}

export function getCurrentLocale(): SupportedLocale {
  if (!i18nInstance) {
    return getLocaleFromUrl()
  }
  return i18nInstance.global.locale.value as SupportedLocale
}
